import { getDisplayName, getFreshlingDisplayName, getFirstName } from './flyerDisplay';
import { getEventDateMergePack } from './eventUtils';

// Escape a string so it can be used literally in a RegExp.
function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Event date for merge tokens (aligned with EmailComposerModal formatEventDate). */
function formatMergeEventDate(dateVal) {
  if (dateVal == null || String(dateVal).trim() === '') return '';
  let d;
  if (dateVal && typeof dateVal.toDate === 'function') {
    d = dateVal.toDate();
  } else if (dateVal instanceof Date) {
    d = dateVal;
  } else {
    d = new Date(String(dateVal).trim());
  }
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Build basic mail-merge data for a given recipient.
 * Supports:
 * - {{name}}
 * - {{team}}
 * - {{mentor}}
 * - {{eventTitle}}
 * - {{subEventLabel}}
 *
 * Attempts to find a flyer object for richer data when possible, using:
 * - explicit flyer argument (if provided)
 * - context.flyer / context.mentorFlyer / context.freshlingFlyer
 * - context.registrants (sub-event)
 * - context.mentors / context.freshlings (team / manifest)
 * - context.sponsorMergeByEmail[email] → sponsorName, sponsorType, sponsorFollowUpDetailsHtml, raffleWinnersHtml, sponsorPrizeSummaryHtml
 * - context.sponsorFollowUpGlobal → event snapshot merge fields (freshlings, teams, sponsorEventSnapshotHtml, …) for sponsor follow-up
 */
export function buildMergeDataForRecipient(context, email, explicitFlyer) {
  const safeEmail = (email || '').toString().trim().toLowerCase();

  let flyer = explicitFlyer || null;

  const tryMatchFlyerList = (list) => {
    if (!Array.isArray(list) || !safeEmail) return null;
    return (
      list.find(
        (f) => (f && (f.email || '').toString().trim().toLowerCase() === safeEmail)
      ) || null
    );
  };

  if (!flyer && context) {
    flyer =
      context.flyer ||
      context.mentorFlyer ||
      context.freshlingFlyer ||
      null;
  }

  if (!flyer && context) {
    flyer =
      tryMatchFlyerList(context.registrants) ||
      tryMatchFlyerList(context.mentors) ||
      tryMatchFlyerList(context.freshlings) ||
      flyer;
  }

  let name = '';
  if (flyer) {
    const first = getFirstName(flyer);
    if (first) {
      name = first;
    } else if (flyer.type === 'freshling') {
      const display = getFreshlingDisplayName(flyer) || flyer.properName || flyer.name || '';
      if (display) {
        name = display.split(/\s+/)[0] || display;
      } else {
        name = flyer.email || '';
      }
    } else {
      const display = getDisplayName(flyer) || flyer.name || '';
      name = display ? display.split(/\s+/)[0] || display : (flyer.email || '');
    }
  }
  // Last resort: if we still have no name, use local part of email (before @) instead of full email
  if (!name && safeEmail) {
    const at = safeEmail.indexOf('@');
    name = at > 0 ? safeEmail.slice(0, at) : safeEmail;
  }

  let team = '';
  if (flyer && flyer.team) {
    team = flyer.team;
  } else if (context && context.team) {
    if (context.team.teamNumber) {
      team = `Team ${context.team.teamNumber}`;
    } else if (context.team.teamName) {
      team = context.team.teamName;
    }
  }

  let mentor = '';
  if (context) {
    if (context.mentorFlyer) {
      mentor = getDisplayName(context.mentorFlyer) || context.mentorFlyer.email || '';
    } else if (context.team && context.team.mentor && Array.isArray(context.mentors)) {
      const mentorFlyer = context.mentors.find(
        (m) =>
          (m.email || '').toString().trim().toLowerCase() ===
          (context.team.mentor || '').toString().trim().toLowerCase()
      );
      if (mentorFlyer) {
        mentor = getDisplayName(mentorFlyer) || mentorFlyer.email || '';
      } else {
        mentor = context.team.mentor;
      }
    }
  }

  const eventTitle =
    (context && context.event && (context.event.title || context.event.name)) || '';

  const subEventLabel = (context && context.subEventLabel) || '';

  // First name for greeting (same as name when name is first name only)
  const firstName = name;

  // Recurrency-specific: check-in time and class time from flyer's chosen slot
  let checkInTime = '';
  let classTime = '';
  if (context && context.event && flyer && flyer.recurrencyUpdate && flyer.recurrencyUpdate.timeSlotId) {
    const slots = context.event.recurrencyTimeSlots || [];
    const slot = slots.find((s) => s.id === flyer.recurrencyUpdate.timeSlotId);
    if (slot) {
      const startRaw = slot.start != null ? String(slot.start).trim() : '';
      classTime = startRaw;

      // Default check-in time: one hour before the class/meet time when parsable.
      if (startRaw) {
        const m = startRaw.match(/^(\d{1,2}):(\d{2})$/);
        if (m) {
          let hour = parseInt(m[1], 10);
          const mins = parseInt(m[2], 10);
          if (!Number.isNaN(hour) && !Number.isNaN(mins)) {
            hour = (hour + 23) % 24; // minus one hour, wrap around midnight
            const hh = String(hour).padStart(2, '0');
            const mm = String(mins).padStart(2, '0');
            checkInTime = `${hh}:${mm}`;
          }
        }
      }

      // If we couldn't derive it from start time, fall back to explicit check-in.
      if (!checkInTime && slot.checkIn) {
        checkInTime = String(slot.checkIn).trim();
      }
    }
  }

  // Recurrency update URL for this event (event-specific link)
  let recurrencyUpdateUrl = '';
  if (context && context.event && context.event.id && typeof window !== 'undefined') {
    const base = (process.env.REACT_APP_SITE_URL || window.location.origin || '').toString().replace(/\/$/, '');
    recurrencyUpdateUrl = base ? `${base}/recurrency-update?event=${context.event.id}` : '';
  }

  // Fury Lab (and Email templates “test send”): participantName, eventDate, meetTime, blockSummary, coachName
  let participantName = '';
  let eventDate = '';
  let meetTime = '';
  let blockSummary = '';
  let coachName = '';
  if (flyer && context && (context.furyLab || context.type === 'templateTest')) {
    participantName = (flyer.name || getDisplayName(flyer) || flyer.email || '').toString().trim();
    meetTime = flyer.meetTime != null && flyer.meetTime !== '' ? String(flyer.meetTime).trim() : '';
    blockSummary =
      flyer.blockSummary != null && flyer.blockSummary !== '' ? String(flyer.blockSummary).trim() : '';
    coachName = flyer.coachName != null && flyer.coachName !== '' ? String(flyer.coachName).trim() : '';
  }

  // Sponsor emails (Prizes tab): per-recipient HTML + company name (context.sponsorMergeByEmail keyed by lowercased email)
  let sponsorName = '';
  let sponsorContactNames = '';
  let sponsorCompanyPossessive = '';
  let sponsorAttachmentUrls = [];
  let raffleWinnersHtml = '';
  let sponsorPrizeSummaryHtml = '';
  let sponsorType = '';
  let sponsorTypeArticle = '';
  let sponsorFollowUpDetailsHtml = '';
  if (context && context.sponsorMergeByEmail && safeEmail) {
    const pack = context.sponsorMergeByEmail[safeEmail];
    if (pack && typeof pack === 'object') {
      sponsorName = (pack.sponsorName != null ? String(pack.sponsorName) : '').trim();
      sponsorContactNames = (pack.sponsorContactNames != null ? String(pack.sponsorContactNames) : '').trim();
      sponsorCompanyPossessive =
        (pack.sponsorCompanyPossessive != null ? String(pack.sponsorCompanyPossessive) : '').trim();
      sponsorAttachmentUrls = Array.isArray(pack.sponsorAttachmentUrls) ? pack.sponsorAttachmentUrls.filter(Boolean) : [];
      raffleWinnersHtml = (pack.raffleWinnersHtml != null ? String(pack.raffleWinnersHtml) : '').trim();
      sponsorPrizeSummaryHtml = (pack.sponsorPrizeSummaryHtml != null ? String(pack.sponsorPrizeSummaryHtml) : '').trim();
      sponsorType = (pack.sponsorType != null ? String(pack.sponsorType) : '').trim();
      sponsorTypeArticle = (pack.sponsorTypeArticle != null ? String(pack.sponsorTypeArticle) : '').trim();
      sponsorFollowUpDetailsHtml =
        (pack.sponsorFollowUpDetailsHtml != null ? String(pack.sponsorFollowUpDetailsHtml) : '').trim();
    }
  }

  const sponsorFollowUpGlobal =
    context && context.sponsorFollowUpGlobal && typeof context.sponsorFollowUpGlobal === 'object'
      ? context.sponsorFollowUpGlobal
      : null;

  const datePack = getEventDateMergePack(context && context.event);

  if (flyer && context && (context.furyLab || context.type === 'templateTest')) {
    const fromLabel = (context.eventDateLabel || '').toString().trim();
    if (!fromLabel) {
      eventDate = datePack.furyLabDate || formatMergeEventDate(context.event.date) || '';
    } else {
      eventDate = fromLabel;
    }
  }

  return {
    name,
    firstName,
    team,
    mentor,
    eventTitle,
    subEventLabel,
    checkInTime: checkInTime || '—',
    classTime: classTime || '—',
    recurrencyUpdateUrl,
    participantName,
    eventDate,
    meetTime,
    blockSummary,
    coachName,
    ...(sponsorFollowUpGlobal || {}),
    sponsorName,
    sponsorContactNames,
    sponsorCompanyPossessive,
    sponsorAttachmentUrls,
    raffleWinnersHtml,
    sponsorPrizeSummaryHtml,
    sponsorType,
    sponsorTypeArticle,
    sponsorFollowUpDetailsHtml,
    ...datePack,
  };
}

/**
 * Apply {{placeholder}} style merges to a string.
 * Supports: {{name}}, {{firstName}}, {{team}}, {{mentor}}, {{eventTitle}}, {{subEventLabel}},
 * {{checkInTime}}, {{classTime}}, {{recurrencyUpdateUrl}},
 * {{participantName}}, {{eventDate}}, {{meetTime}}, {{blockSummary}}, {{coachName}} (Fury Lab),
 * {{eventWeekRange}}, {{eventDateRange}}, {{recurrencyDate}}, {{recurrencyWeekday}}, {{furyLabDate}},
 * {{waterTrainingThursdayDate}}, {{waterTrainingFridayDate}}, {{canopyThursdayDate}}, {{canopyFridayDate}},
 * {{highAltitudeDate}}, {{afterPartyDate}}, {{activityDatesHtml}},
 * {{sponsorName}}, {{SponsorCompanyName}}, {{sponsorType}}, {{sponsorFollowUpDetailsHtml}}, {{sponsorEventSnapshotHtml}},
 * {{freshlings}}, {{teams}}, {{mentors}}, {{avgJumps}}, {{states}}, {{countries}}, {{ageRange}}, {{highAltitude}},
 * {{jetJumps}}, {{canopyCourses}}, {{waterTraining}}, {{raffleWinnersHtml}} (sponsor raffle winner notices).
 */
export function applyMerge(template, mergeData) {
  if (!template || typeof template !== 'string' || !mergeData) return template || '';
  let out = template;
  const sn = mergeData.sponsorName != null ? String(mergeData.sponsorName) : '';
  const mapping = {
    '{{name}}': mergeData.name || '',
    '{{firstName}}': mergeData.firstName != null ? mergeData.firstName : (mergeData.name || ''),
    '{{team}}': mergeData.team || '',
    '{{mentor}}': mergeData.mentor || '',
    '{{eventTitle}}': mergeData.eventTitle || '',
    '{{subEventLabel}}': mergeData.subEventLabel || '',
    '{{checkInTime}}': mergeData.checkInTime != null ? String(mergeData.checkInTime) : '—',
    '{{classTime}}': mergeData.classTime != null ? String(mergeData.classTime) : '—',
    '{{recurrencyUpdateUrl}}': mergeData.recurrencyUpdateUrl || '',
    '{{participantName}}': mergeData.participantName != null ? String(mergeData.participantName) : '',
    '{{eventDate}}': mergeData.eventDate != null ? String(mergeData.eventDate) : '',
    '{{meetTime}}': mergeData.meetTime != null ? String(mergeData.meetTime) : '',
    '{{blockSummary}}': mergeData.blockSummary != null ? String(mergeData.blockSummary) : '',
    '{{coachName}}': mergeData.coachName != null ? String(mergeData.coachName) : '',
    '{{sponsorName}}': sn,
    '{{SponsorCompanyName}}': sn,
    '{{SponsorContactNames}}': mergeData.sponsorContactNames != null ? String(mergeData.sponsorContactNames) : sn,
    '{{SponsorCompanyPossessive}}':
      mergeData.sponsorCompanyPossessive != null ? String(mergeData.sponsorCompanyPossessive) : '',
    '{{sponsorType}}': mergeData.sponsorType != null ? String(mergeData.sponsorType) : '',
    '{{sponsorTypeArticle}}': mergeData.sponsorTypeArticle != null ? String(mergeData.sponsorTypeArticle) : '',
    '{{sponsorFollowUpDetailsHtml}}':
      mergeData.sponsorFollowUpDetailsHtml != null ? String(mergeData.sponsorFollowUpDetailsHtml) : '',
    '{{sponsorEventSnapshotHtml}}':
      mergeData.sponsorEventSnapshotHtml != null ? String(mergeData.sponsorEventSnapshotHtml) : '',
    '{{freshlings}}': mergeData.freshlings != null ? String(mergeData.freshlings) : '',
    '{{teams}}': mergeData.teams != null ? String(mergeData.teams) : '',
    '{{mentors}}': mergeData.mentors != null ? String(mergeData.mentors) : '',
    '{{avgJumps}}': mergeData.avgJumps != null ? String(mergeData.avgJumps) : '',
    '{{states}}': mergeData.states != null ? String(mergeData.states) : '',
    '{{countries}}': mergeData.countries != null ? String(mergeData.countries) : '',
    '{{ageRange}}': mergeData.ageRange != null ? String(mergeData.ageRange) : '',
    '{{highAltitude}}': mergeData.highAltitude != null ? String(mergeData.highAltitude) : '',
    '{{jetJumps}}': mergeData.jetJumps != null ? String(mergeData.jetJumps) : '',
    '{{canopyCourses}}': mergeData.canopyCourses != null ? String(mergeData.canopyCourses) : '',
    '{{waterTraining}}': mergeData.waterTraining != null ? String(mergeData.waterTraining) : '',
    '{{raffleWinnersHtml}}': mergeData.raffleWinnersHtml != null ? String(mergeData.raffleWinnersHtml) : '',
    '{{sponsorPrizeSummaryHtml}}':
      mergeData.sponsorPrizeSummaryHtml != null ? String(mergeData.sponsorPrizeSummaryHtml) : '',
    '{{eventWeekRange}}': mergeData.eventWeekRange != null ? String(mergeData.eventWeekRange) : '',
    '{{eventDateRange}}': mergeData.eventDateRange != null ? String(mergeData.eventDateRange) : '',
    '{{recurrencyDate}}': mergeData.recurrencyDate != null ? String(mergeData.recurrencyDate) : '',
    '{{recurrencyWeekday}}': mergeData.recurrencyWeekday != null ? String(mergeData.recurrencyWeekday) : '',
    '{{furyLabDate}}': mergeData.furyLabDate != null ? String(mergeData.furyLabDate) : '',
    '{{waterTrainingThursdayDate}}':
      mergeData.waterTrainingThursdayDate != null ? String(mergeData.waterTrainingThursdayDate) : '',
    '{{waterTrainingFridayDate}}':
      mergeData.waterTrainingFridayDate != null ? String(mergeData.waterTrainingFridayDate) : '',
    '{{canopyThursdayDate}}': mergeData.canopyThursdayDate != null ? String(mergeData.canopyThursdayDate) : '',
    '{{canopyFridayDate}}': mergeData.canopyFridayDate != null ? String(mergeData.canopyFridayDate) : '',
    '{{highAltitudeDate}}': mergeData.highAltitudeDate != null ? String(mergeData.highAltitudeDate) : '',
    '{{afterPartyDate}}': mergeData.afterPartyDate != null ? String(mergeData.afterPartyDate) : '',
    '{{activityDatesHtml}}': mergeData.activityDatesHtml != null ? String(mergeData.activityDatesHtml) : '',
  };
  Object.keys(mapping).forEach((token) => {
    const value = mapping[token] != null ? String(mapping[token]) : '';
    out = out.replace(new RegExp(escapeRegExp(token), 'g'), value);
  });
  return out;
}

